'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  toast,
  Popover,
  StatusIcon,
  Modal,
  Separator,
  QuantityStepper,
} from '@mokoboko/components';
import { ERROR_STATUS_ICON_CLASS } from '@/lib/error-ramp';
import { X, CreditCard, Gift, Layers, UserPlus, Users } from 'lucide-react';
import { LockIcon, RefreshIcon } from '@/icons';
import {
  LockedPopoverContent,
  SubstituteBadge,
} from '@/components';
import {
  buildInstructorName,
  formatClassDateTime,
  formatDateWithConditionalYear,
  formatAmount,
  formatHourCount,
  getUserTimezone,
  mergeClassNames,
} from '@/lib/utils';
import { isOneTimeClass, resolveBookingRule } from '@/helpers/class';
import { getTenantDirectionsUrl } from '@/helpers/tenant';
import {
  classTimeGiftSpend,
  giftSpendDisplay,
  refundsToGiftCard,
  wholeOrderGiftBalanceLine,
} from '@/helpers/gift-card';
import {
  classCalendarFileUrl,
  classRaceRefundNotice,
  getOccurrenceId,
  isCreditBooking,
  isStaleBookingRefusal,
  orderReceiptPath,
  RACE_REFUND_RECEIPT,
  selectReceiptItems,
  withReceiptClassTime,
  type ReceiptClassTime,
} from '@/helpers/order';
import type { GuestPassAdd, Order, ReceiptGuest } from '@/lib/types';
import { getErrorCode, getErrorMessage } from '@/lib/error-utils';
import { isLoginRequired } from '@/lib/http-adapter';
import { cancelClass } from '@/services/order';
import { forgetClassAvailabilities } from '@/state/class-availabilities';
import { useAuth } from '@/contexts/auth';
import { useHasPassed } from '@/hooks/use-has-passed';
import { LoginDialog } from '@/components/dialogs/login';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';
import useTenant, { useTimeFormat } from '@/state/tenant';
import useTopupCodes from '@/state/topup-code';
import { CalendarIntegration } from '@/components/calendar-integration';
import { InstallAppCard } from '@/components/features/home-screen-app/install-app-card';
import { getSubscriptionRenewalLabel } from '@/helpers/status';
import {
  getAvailableCredits,
  getAvailableSubCredits,
  getCancelClassBodyCopy,
  getCancelRefundCopy,
  getNoCreditReturnedReason,
  getSpendableClassesLeft,
  isCreditReturnedOnCancel,
  isNoCreditReturned,
  pluralizeCredits,
  sumActiveTopupCreditsForPlan,
} from '@/helpers/credits';
import {
  buildMinSpotsAllNotice,
  getMinSpotsPerBooking,
} from '@/helpers/min-spots';
import {
  apartGuestsHeading,
  bookingSizeText,
  bringGuestHint,
  canRemoveApartGuest,
  canRemoveGuest,
  cancelGuestsLabel,
  cancelGuestsSubtext,
  cancelModalTitle,
  cancelledGuestsSentence,
  creditsAndPassesReturnLine,
  guestAddFor,
  guestOfCopy,
  guestsAddedToast,
  guestsApartFromHost,
  guestsAtClassTime,
  guestsMustCancel,
  withGuestPasses,
} from '@/helpers/guest-pass';

import { AddGuestModal } from './add-guest-modal';
import {
  ReceiptDateRow,
  ReceiptGuestRow,
  ReceiptLocationRow,
  ReceiptNotice,
  ReceiptRow,
  ReceiptStatusHeader,
  type ReceiptStatus,
} from './receipt-rows';
import { RemoveGuestModal } from './remove-guest-modal';

// The window the receipt's own Cancel and Reschedule buttons follow.
const isBeforeChangeCutoff = (
  start: Date,
  noticeHours: number | null | undefined
): boolean => start.getTime() - (noticeHours ?? 0) * 3_600_000 > Date.now();

const LockedAction = ({
  label,
  notice,
  open,
  onOpenChange,
}: {
  label: string;
  notice: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <Popover.Root open={open} onOpenChange={onOpenChange}>
    <Popover.Trigger asChild>
      <span className="inline-flex flex-1">
        <Button
          variant="secondary-gray"
          leadingIcon={<LockIcon />}
          iconClassName="[&_svg]:!w-[18px] [&_svg]:!h-[18px]"
          size="lg"
          className="pointer-events-none w-full justify-center shadow-xs"
          disabled
        >
          {label}
        </Button>
      </span>
    </Popover.Trigger>
    <LockedPopoverContent side="bottom">{notice}</LockedPopoverContent>
  </Popover.Root>
);

interface CancelClassModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  activeQuantity: number;
  minSpots: number;
  totalQuantity: number;
  cancelSpots: number;
  onCancelQuantityChange: (val: number) => void;
  isCreditCodeApplied: boolean;
  isFromPack: boolean;
  isCreditSourceActive: boolean;
  isTopupBooking: boolean;
  isTopupRefundable: boolean;
  classPackClassesLeft: number | null;
  subscriptionClassesLeft: number | null;
  topupCredits: number;
  isFreeBooking: boolean;
  giftCardRemainder: number | null;
  giftCardRefundsToCard: boolean;
  // First names of the guests at this class time; empty without guests.
  guestNames: string[];
  cancelGuests: boolean;
  onCancelGuestsChange: (value: boolean) => void;
  // Set when the class's minimum forces the guests out with the host.
  cancelGuestsLocked: boolean;
  guestPassReturns: boolean;
  onConfirm: () => void;
  isProcessing: boolean;
}

const CancelClassModal = ({
  isOpen,
  onOpenChange,
  activeQuantity,
  minSpots,
  totalQuantity,
  cancelSpots,
  onCancelQuantityChange,
  isCreditCodeApplied,
  isFromPack,
  isCreditSourceActive,
  isTopupBooking,
  isTopupRefundable,
  classPackClassesLeft,
  subscriptionClassesLeft,
  topupCredits,
  isFreeBooking,
  giftCardRemainder,
  giftCardRefundsToCard,
  guestNames,
  cancelGuests,
  onCancelGuestsChange,
  cancelGuestsLocked,
  guestPassReturns,
  onConfirm,
  isProcessing,
}: CancelClassModalProps) => {
  // A class with a minimum is canceled whole: a partial cancel would leave a
  // booking below it.
  const showStepper = activeQuantity > 1 && minSpots <= 1;
  const hasGuests = guestNames.length > 0;
  const hasBody = showStepper || hasGuests;
  const guestsCancelled = cancelGuests ? guestNames.length : 0;
  // Said here once: the guest box only says the guests go too.
  const minSpotsNotice =
    minSpots > 1 && (activeQuantity > 1 || cancelGuestsLocked)
      ? buildMinSpotsAllNotice(
          minSpots,
          activeQuantity + guestsCancelled,
          'canceled'
        )
      : null;
  const isPartial = showStepper && cancelSpots < activeQuantity;
  const spotsCancelled = cancelSpots + guestsCancelled;
  const spotLabel = `${spotsCancelled} spot${spotsCancelled > 1 ? 's' : ''}`;

  const title = cancelModalTitle({
    spotsCancelled,
    spotsHeld: activeQuantity + guestsCancelled,
    isPartial,
  });

  const passesBack = guestPassReturns ? guestsCancelled : 0;
  const creditsComeBack = isCreditReturnedOnCancel({
    isCreditCodeApplied,
    isTopupBooking,
    isCreditSourceActive,
    isFromPack,
    subscriptionClassesLeft,
  });
  const bodyReturnsPasses = creditsComeBack && passesBack > 0;
  const body = bodyReturnsPasses
    ? creditsAndPassesReturnLine(
        cancelSpots,
        passesBack,
        isFromPack ? 'pack' : 'membership'
      )
    : getCancelClassBodyCopy({
        k: cancelSpots,
        isCreditCodeApplied,
        isFromPack,
        isCreditSourceActive,
        isTopupBooking,
        isTopupRefundable,
        classPackClassesLeft,
        subscriptionClassesLeft,
        topupCredits,
        isFreeBooking,
        giftCardRemainder,
        giftCardRefundsToCard,
      });

  const noCreditReturned =
    isCreditCodeApplied &&
    isNoCreditReturned(isTopupBooking, isTopupRefundable, isCreditSourceActive);
  const confirmLabel = isPartial
    ? `Cancel ${spotLabel}`
    : noCreditReturned
      ? 'Cancel anyway'
      : 'Cancel booking';

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && isProcessing) return;
        onOpenChange(open);
      }}
    >
      <Modal.Content size="sm" hasCloseButton={false}>
        <Modal.Header
          className={mergeClassNames('border-b-0', hasBody && '!pb-0')}
        >
          <div className="mb-3">
            <StatusIcon
              variant="error"
              size="xl"
              className={ERROR_STATUS_ICON_CLASS}
            />
          </div>
          <Modal.Title>{title}</Modal.Title>
          <Modal.Description>
            {body}
            {minSpotsNotice && ` ${minSpotsNotice}`}
          </Modal.Description>
        </Modal.Header>
        {hasBody && (
          <Modal.Body>
            <div className="flex flex-col gap-5">
              <Separator />
              {showStepper && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">
                    Spots to cancel
                  </span>
                  <QuantityStepper
                    value={cancelSpots}
                    max={activeQuantity}
                    onValueChange={onCancelQuantityChange}
                    decreaseLabel="Decrease spots to cancel"
                    increaseLabel="Increase spots to cancel"
                  />
                </div>
              )}
              {hasGuests && (
                <Checkbox
                  checked={cancelGuests}
                  disabled={cancelGuestsLocked}
                  onCheckedChange={(value) =>
                    onCancelGuestsChange(value === true)
                  }
                  label={cancelGuestsLabel(guestNames.length)}
                  subtext={cancelGuestsSubtext({
                    names: guestNames,
                    checked: cancelGuests,
                    passReturns: guestPassReturns && !bodyReturnsPasses,
                  })}
                />
              )}
            </div>
            {showStepper && (
              <p className="sr-only">
                Out of {totalQuantity} booked, {activeQuantity} active.
              </p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Modal.Close asChild>
            <Button
              variant="secondary-gray"
              onClick={() => onOpenChange(false)}
              disabled={isProcessing}
              className="ml-auto"
            >
              Keep my booking
            </Button>
          </Modal.Close>
          <Button
            variant="primary"
            onClick={onConfirm}
            isProcessing={isProcessing}
          >
            {confirmLabel}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};

type PendingAction = 'reschedule' | 'cancel' | { remove: ReceiptGuest };

interface ClassOrderReceiptProps {
  order: Order;
  rescheduled?: boolean;
  cancelled?: boolean;
  // For callers that own the order outside React Query, where invalidating
  // ['order', id] refreshes nothing after a partial cancel.
  onOrderChange?: () => void;
}

const ClassOrderReceiptBody = ({
  order,
  rescheduled = false,
  cancelled: cancelledParam = false,
  onOrderChange,
  classTime,
}: ClassOrderReceiptProps & { classTime: ReceiptClassTime }) => {
  const { data: tenant } = useTenant();
  const timeFormat = useTimeFormat();
  const { isAuthenticated, user } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const {
    order_id,
    coupon_code,
    coupon_type,
    class_pack_code,
    subscription_code,
  } = order;
  const { items: slotItems, head: orderItem, activeItems } = classTime;
  const activeQuantity = activeItems.length;
  // Revisiting a cancelled booking carries no ?cancelled=true, yet every item
  // is CANCELLED — without this the receipt reads Confirmed and re-offers cancel.
  const cancelled = cancelledParam || activeQuantity === 0;
  const classData = orderItem.class;
  const isOneTime = isOneTimeClass(classData);
  // An absent class_bookable reads as bookable.
  const canRebookSame = !isOneTime && order.class_bookable !== false;
  const directionsUrl = getTenantDirectionsUrl(tenant, classData.location);
  const instructor = orderItem.instructor_schedule.admin_user;
  const substitute = orderItem.substitute_admin_user;
  const originalInstructorName = buildInstructorName(
    instructor?.first_name,
    instructor?.last_name
  );
  const displayInstructorName = substitute
    ? buildInstructorName(substitute.first_name, substitute.last_name)
    : originalInstructorName;
  // Someone else's booking, opened from a forwarded claim link: it shows how
  // it was paid, never the booker's codes, balances or controls.
  const isLinkHolder = order.viewer === 'LINK_HOLDER';
  // A guest's own booking: the host is the only one who can change it.
  const guestOf = order.guest_of ?? null;
  const guestsHere = guestsAtClassTime(order.guests, classTime);
  const apartGuests = guestsApartFromHost(
    order.guests,
    order.class_order_items ?? []
  );
  const minSpots = getMinSpotsPerBooking(classData);
  const creditCode = class_pack_code || subscription_code;
  const isCreditCodeApplied = isCreditBooking(order);
  const isFromPack = !!class_pack_code;
  const isUnlimitedSubscription =
    !isFromPack && subscription_code?.classes_left === null;

  const isSubscriptionWithinPeriod =
    !!subscription_code?.current_period_end &&
    new Date(subscription_code.current_period_end) > new Date();

  // The backend's own return rule; the guess below when it gives no answer
  // (an older backend, or no paid pack or membership code).
  const isCreditSourceActive =
    order.returns_to_code ??
    (creditCode?.status === 'ACTIVE' ||
      (isFromPack && class_pack_code?.status !== 'EXPIRED') ||
      isSubscriptionWithinPeriod);

  // The hydrated plan's own code matches coupon_code for plan bookings; a
  // mismatch means the redeemed code was a top-up attached to that plan.
  const isTopupBooking =
    isCreditCodeApplied && !!creditCode && coupon_code !== creditCode.code;
  // Mirrors the backend's expires_at-only refund rule. Optimistic when the
  // backend doesn't return topup_code yet.
  const isTopupRefundable =
    !order.topup_code?.expires_at ||
    new Date(order.topup_code.expires_at) > new Date();

  const { data: topupCodes } = useTopupCodes({
    userId:
      !isLinkHolder && isCreditCodeApplied && !isUnlimitedSubscription
        ? (order.user_id ?? undefined)
        : undefined,
  });
  const { credits: topupCreditsForPlan, neverExpires: topupsNeverExpire } =
    sumActiveTopupCreditsForPlan(
      topupCodes,
      isFromPack ? 'PACK' : 'SUBSCRIPTION',
      isFromPack
        ? class_pack_code?.class_pack_code_id
        : subscription_code?.subscription_code_id
    );

  // Plan credits the customer can still spend, matching the dashboard's
  // credit summary card: dead plans (expired pack, lapsed canceled sub,
  // forfeited paused RESET sub) count 0 even when classes_left is not.
  const spendablePlanCredits = class_pack_code
    ? class_pack_code.status === 'ACTIVE' ||
      class_pack_code.status === 'FULLY_USED'
      ? getAvailableCredits(class_pack_code)
      : 0
    : subscription_code
      ? (getSpendableClassesLeft(subscription_code) ?? 0)
      : 0;

  const hasCoupon = !!(
    coupon_code &&
    (coupon_type === 'STRIPE' || coupon_type === 'FREE')
  );
  const quantity = slotItems.length;
  // Cancelled items stay in the payload, so a partly cancelled slot must count
  // only the spots still held. A fully cancelled slot has none left, so count
  // its cancelled ones instead — that is what was refunded.
  const pricedItems = activeQuantity > 0 ? activeItems : slotItems;
  const pricedQuantity = pricedItems.length;
  // Cancelling an expired credit source voids the credit instead of
  // refunding it; the cancelled headline must not claim otherwise.
  const noCreditRefunded = isNoCreditReturned(
    isTopupBooking,
    isTopupRefundable,
    isCreditSourceActive
  );
  const noCreditRefundedReason = getNoCreditReturnedReason(
    isTopupBooking,
    isFromPack
  );
  const creditStatusLabel = isLinkHolder
    ? 'Booked with credits'
    : isUnlimitedSubscription
      ? cancelled
        ? 'No credit was used'
        : 'Included in your plan'
      : cancelled && noCreditRefunded
        ? 'No credit refunded'
        : `${withGuestPasses(
            `${pricedQuantity} ${isTopupBooking ? 'top-up ' : ''}${pluralizeCredits(pricedQuantity)}`,
            cancelled ? 0 : guestsHere.length
          )} ${cancelled ? 'refunded' : 'used'}`;
  const unlimitedSizeText = bookingSizeText(activeQuantity, guestsHere.length);
  // Per-slot price = sum of the slot's items' discounted_price. Order.final_amount
  // is the gross order total — would over-count when the order spans slots.
  const slotAmount = pricedItems.reduce(
    (sum, item) => sum + Number(item.discounted_price ?? 0),
    0
  );
  const slotOriginalAmount = pricedItems.reduce(
    (sum, item) => sum + Number(item.original_price ?? 0),
    0
  );
  // The backend writes each item's refund_amount in the same transaction as the
  // cancellation, and only for what Stripe really refunded, so this is the one
  // refund figure on the page that was observed rather than worked out here.
  const slotRefundedAmount = slotItems.reduce(
    (sum, item) => sum + Number(item.refund_amount ?? 0),
    0
  );
  // A booking a gift card paid in full also charges nothing, but it was not
  // free.
  const isFreeBooking =
    (coupon_type === 'FREE' || order.final_amount === 0) && !order.gift_card;
  const giftSpend =
    order.gift_card &&
    classTimeGiftSpend(order.gift_card, pricedItems, slotItems);
  const giftCardRefundsToCard = refundsToGiftCard(order.gift_card);
  const giftSpendLines =
    giftSpend && giftSpendDisplay(giftSpend, order.currency);
  // The balance is the card's right after this booking (D124). Once any spot
  // of the order is cancelled, at whichever class time, part of the share is
  // back on the card, so it no longer is. A full dashboard refund puts the
  // whole share back with every spot still booked (G60).
  const giftSubline =
    giftSpend &&
    giftSpendLines &&
    [
      giftSpend.refunded > 0 &&
        `${formatAmount(giftSpend.refunded, order.currency)} back on your gift card`,
      !order.class_order_items?.some(
        (item) => item.booking_status === 'CANCELLED'
      ) && wholeOrderGiftBalanceLine(giftSpendLines, order.payment_status),
      giftSpend.remainder === 0 &&
        !cancelled &&
        activeQuantity > 1 &&
        `${activeQuantity} spots`,
    ]
      .filter(Boolean)
      .join(' · ');
  const startDate = new Date(orderItem.start_time);
  const endDate = new Date(orderItem.end_time);
  const classEnded = useHasPassed(endDate);
  const completed = !cancelled && classEnded;
  // ?rescheduled=true stays in the URL, so it only speaks for the class until
  // the class ends.
  const status: ReceiptStatus = cancelled
    ? 'cancelled'
    : completed
      ? 'completed'
      : rescheduled
        ? 'rescheduled'
        : 'confirmed';
  const subscriptionRenewalLabel =
    !isFromPack && subscription_code
      ? getSubscriptionRenewalLabel(subscription_code)
      : null;
  // The plan date describes every remaining credit except never-expiring
  // top-ups; only when those are all that's left would it mislead.
  const showTopupNeverExpires =
    isTopupBooking && spendablePlanCredits === 0 && topupsNeverExpire;

  // Expiry/renewal half of the credit subline. Null for an ended unlimited sub
  // (backend nulls current_period_end on force-cancel) — the separator must
  // drop with it, otherwise the subline reads "Unlimited ·" with nothing after.
  const creditExpirySuffix = showTopupNeverExpires
    ? 'Never expires'
    : isFromPack
      ? creditCode?.expires_at
        ? `Expires ${formatDateWithConditionalYear(creditCode.expires_at)}`
        : 'No expiry'
      : subscriptionRenewalLabel && subscription_code?.current_period_end
        ? `${subscriptionRenewalLabel} ${formatDateWithConditionalYear(subscription_code.current_period_end)}`
        : null;

  const [isCancelClassModalOpen, setIsCancelClassModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelQuantity, setCancelQuantity] = useState(1);
  // Null until the host touches the box: it then follows whether the cancel
  // takes every spot, since a host keeping a spot is likely still coming.
  const [cancelGuestsChoice, setCancelGuestsChoice] = useState<boolean | null>(
    null
  );
  const [removingGuest, setRemovingGuest] = useState<ReceiptGuest | null>(null);
  // Held from the click, so a re-read while the modal is open cannot move it
  // to another class time.
  const [addGuestTarget, setAddGuestTarget] = useState<
    GuestPassAdd['class_times'][number] | null
  >(null);
  const [openPopover, setOpenPopover] = useState<
    'reschedule' | 'cancel' | null
  >(null);
  const [activeModal, setActiveModal] = useState<'login' | null>(null);
  const pendingActionRef = useRef<PendingAction | null>(null);

  const hasClassStarted = startDate <= new Date();

  const modificationNoticeHours = resolveBookingRule(
    'modification_notice_hours',
    classData,
    tenant?.schedule_config
  );
  // A one-time class has no other date to move to, so the locked-window notices
  // must not name a change the customer was never offered.
  const changeVerb = isOneTime ? 'canceled' : 'canceled or rescheduled';
  const noticeHours = modificationNoticeHours
    ? formatHourCount(modificationNoticeHours)
    : null;
  const lockedNotice = hasClassStarted
    ? 'This class has already started.'
    : `A booking can't be ${changeVerb} if it's less than ${noticeHours} before the class starts.`;

  const showActionButtons = isBeforeChangeCutoff(
    startDate,
    modificationNoticeHours
  );

  const cancelQty = Math.min(Math.max(cancelQuantity, 1), activeQuantity || 1);
  const hostSpotsLeftAfterCancel = Math.max(activeQuantity - cancelQty, 0);
  const cancelGuestsLocked = guestsMustCancel({
    minSpots,
    hostSpotsLeft: hostSpotsLeftAfterCancel,
    activeGuests: guestsHere.length,
  });
  const cancelGuests =
    cancelGuestsLocked ||
    (cancelGuestsChoice ?? hostSpotsLeftAfterCancel === 0);
  const canRemoveGuests =
    !isLinkHolder &&
    showActionButtons &&
    canRemoveGuest({
      minSpots,
      hostSpots: activeQuantity,
      activeGuests: guestsHere.length,
    });
  const guestAdd =
    !cancelled && !completed && !isLinkHolder && !guestOf
      ? guestAddFor(order.guest_pass_add, classTime)
      : null;

  // Managing a booking needs a session; the receipt itself is reachable with a
  // Stripe session_id or a receipt token, so a guest can be looking at it.
  const handleRescheduleClass = () => {
    if (!isAuthenticated) {
      pendingActionRef.current = 'reschedule';
      setActiveModal('login');
      return;
    }

    const query = new URLSearchParams({
      orderId: order_id,
      instructorScheduleId:
        orderItem.instructor_schedule.instructor_schedule_id,
      action: 'reschedule',
      startTime: orderItem.start_time,
      activeQuantity: String(activeQuantity),
    });
    if (guestsHere.length) query.set('guests', String(guestsHere.length));
    const occurrenceId = getOccurrenceId(orderItem);
    if (occurrenceId) query.set('slotId', occurrenceId);
    if (orderItem.slot_id) {
      query.set('fromSlotId', orderItem.slot_id);
    } else if (orderItem.override_slot_id) {
      query.set('fromOverrideSlotId', orderItem.override_slot_id);
    }

    router.push(`/classes/${classData.slug}?${query.toString()}`);
  };

  const openCancelModal = () => {
    if (!isAuthenticated) {
      pendingActionRef.current = 'cancel';
      setActiveModal('login');
      return;
    }

    setCancelQuantity(activeQuantity);
    setCancelGuestsChoice(null);
    setIsCancelClassModalOpen(true);
  };

  const openRemoveGuest = (guest: ReceiptGuest) => {
    if (!isAuthenticated) {
      pendingActionRef.current = { remove: guest };
      setActiveModal('login');
      return;
    }

    setRemovingGuest(guest);
  };

  // Resumed from the auth transition rather than the dialog's onConfirm: the
  // context has not re-rendered by then, so the handlers would still see a
  // guest and re-open the dialog.
  useEffect(() => {
    if (!isAuthenticated || !pendingActionRef.current) return;

    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action === 'reschedule') {
      handleRescheduleClass();
    } else if (action === 'cancel') {
      openCancelModal();
    } else {
      openRemoveGuest(action.remove);
    }
  }, [isAuthenticated]);

  // Guests never touch top-up credits, so a guest change skips topupCodes.
  const invalidateAfterGuestChange = () => {
    queryClient.invalidateQueries({ queryKey: ['bookedClasses'] });
    queryClient.invalidateQueries({ queryKey: ['order', order_id] });
    if (isCreditCodeApplied) {
      queryClient.invalidateQueries({
        queryKey: [isFromPack ? 'classPackCodes' : 'subscriptionCodes'],
      });
    }
    onOrderChange?.();
  };

  const invalidateAfterMutation = () => {
    invalidateAfterGuestChange();
    if (isCreditCodeApplied) {
      queryClient.invalidateQueries({ queryKey: ['topupCodes'] });
    }
  };

  const handleCancelClass = async () => {
    try {
      setIsCancelling(true);
      const isFullCancel = cancelQty >= activeQuantity;
      const cancelResult = await cancelClass(order_id, {
        quantity: cancelQty,
        ...(guestsHere.length ? { cancel_guests: cancelGuests } : {}),
        ...(orderItem.slot_id
          ? { from_slot_id: orderItem.slot_id }
          : orderItem.override_slot_id
            ? { from_override_slot_id: orderItem.override_slot_id }
            : {}),
        ...(orderItem.slot_id || orderItem.override_slot_id
          ? { from_start_time: orderItem.start_time }
          : {}),
      });

      track({
        event: AnalyticsEvent.CLASS_BOOKING_CANCELED,
        properties: {
          class_name: classData.name,
          order_id,
        },
      });

      const spotLabel = `${cancelQty} spot${cancelQty > 1 ? 's' : ''}`;
      const creditLabel = `${cancelQty} ${pluralizeCredits(cancelQty)}`;
      // What Stripe actually refunded, which the backend caps against the
      // order's remaining refundable total and reports as 0 when the refund
      // itself threw. A figure worked out from the item price is a guess in
      // both of those cases.
      const refundedAmount = Number(cancelResult?.refunded_amount ?? 0);

      let description: string | undefined;
      if (!isUnlimitedSubscription) {
        if (isCreditCodeApplied) {
          if (isTopupBooking) {
            description = isTopupRefundable
              ? `${creditLabel} returned to your top-up balance.`
              : `No credit refunded. ${getNoCreditReturnedReason(isTopupBooking, isFromPack)}`;
          } else if (isCreditSourceActive) {
            description = `${creditLabel} refunded`;
          } else {
            description = `No credit refunded. ${getNoCreditReturnedReason(isTopupBooking, isFromPack)}`;
          }
        } else if (isFreeBooking) {
          description = 'This booking was free, so there is nothing to refund.';
        } else {
          description = getCancelRefundCopy(
            refundedAmount,
            Number(cancelResult?.gift_card_refunded_amount ?? 0),
            order.currency,
            giftCardRefundsToCard,
            giftSpend?.remainder ?? null
          );
        }
      }

      const guestsSentence = cancelledGuestsSentence(
        cancelResult?.cancelled_guest_order_ids?.length ?? 0,
        cancelResult?.guest_passes_returned
      );
      if (guestsSentence) {
        description = description
          ? `${/[.!?]$/.test(description) ? description : `${description}.`} ${guestsSentence}`
          : guestsSentence;
      }

      toast.success(
        isFullCancel ? 'Booking canceled' : `${spotLabel} canceled`,
        description ? { description } : undefined
      );

      invalidateAfterMutation();
      setIsCancelClassModalOpen(false);

      if (isFullCancel) {
        router.replace(
          orderReceiptPath(
            order_id,
            searchParams?.get('receipt_token'),
            classTime,
            'cancelled'
          )
        );
      }
    } catch (error: any) {
      // Covers a session that expired between opening the receipt and cancelling:
      // the adapter only refresh-retries GETs, so this surfaces as a 401 here.
      if (isLoginRequired(error)) {
        setIsCancelClassModalOpen(false);
        setActiveModal('login');
        return;
      }
      // A retry would send the same ids. Whatever replaced them may have
      // replaced the class's times too, so the order and the grid the
      // Reschedule button opens are both read afresh. The URL is pinned to
      // this class time first: otherwise the re-read can land on another class
      // time, and her second try would cancel that one. Native history rather
      // than the router, because this receipt also renders under
      // /order-confirmed, whose query and landing handling must not restart;
      // Next keeps useSearchParams in sync with replaceState.
      if (isStaleBookingRefusal(error)) {
        setIsCancelClassModalOpen(false);
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}?${withReceiptClassTime(window.location.search, classTime)}`
        );
        forgetClassAvailabilities(queryClient);
        invalidateAfterMutation();
        toast.error('This booking has changed. Please try again.');
        return;
      }
      // The class started while the receipt was open, and the re-render this
      // catch ends with hides the buttons.
      if (getErrorCode(error) === 'CLASS_ALREADY_STARTED') {
        setIsCancelClassModalOpen(false);
        toast.error(getErrorMessage(error, 'This class has already started.'));
        return;
      }
      toast.error('Unable to cancel booking, please try again later!');
    } finally {
      setIsCancelling(false);
    }
  };

  const guestOfRow = guestOf
    ? guestOfCopy(
        guestOf,
        cancelled ? 'cancelled' : completed ? 'completed' : 'upcoming',
        !isOneTime
      )
    : null;

  return (
    <>
      <div className="bg-white">
        <ReceiptStatusHeader status={status} title={classData.name}>
          <p className="mt-1 text-sm text-gray-700">
            with {displayInstructorName}
            {substitute && <SubstituteBadge className="ml-1.5 align-middle" />}
          </p>
          {substitute && (
            <p className="mt-1.5 text-sm text-gray-700">
              Covering for {originalInstructorName}
            </p>
          )}
        </ReceiptStatusHeader>

        <Separator className="my-6" />

        <div className="flex flex-col gap-5">
          <ReceiptDateRow
            startDate={startDate}
            endDate={endDate}
            cancelled={cancelled}
            completed={completed}
          />

          {classData.location && (
            <ReceiptLocationRow
              location={classData.location}
              directionsUrl={directionsUrl}
              cancelled={cancelled}
            />
          )}

          {status !== 'rescheduled' && giftSpendLines && (
            <ReceiptRow
              icon={Gift}
              strikethrough={cancelled}
              title={`Gift card ${giftSpendLines.applied}`}
            >
              {giftSubline && (
                <p className="mt-1 text-sm text-gray-600">{giftSubline}</p>
              )}
            </ReceiptRow>
          )}

          {guestOfRow ? (
            <ReceiptRow icon={Users} title={guestOfRow.title}>
              {guestOfRow.note && (
                <p className="mt-1 text-sm text-gray-600">{guestOfRow.note}</p>
              )}
            </ReceiptRow>
          ) : (
            status !== 'rescheduled' &&
            (isCreditCodeApplied ? (
              <ReceiptRow icon={Layers} title={creditStatusLabel}>
                {cancelled &&
                  !isUnlimitedSubscription &&
                  noCreditRefunded &&
                  creditCode && (
                    <p className="mt-1 text-sm text-gray-600">
                      {noCreditRefundedReason}
                    </p>
                  )}
                {creditCode &&
                  !completed &&
                  (!cancelled || isUnlimitedSubscription) && (
                    <p className="mt-1 text-sm text-gray-600">
                      {isUnlimitedSubscription
                        ? 'Unlimited'
                        : `${spendablePlanCredits + topupCreditsForPlan} remaining`}
                      {isUnlimitedSubscription &&
                        !cancelled &&
                        unlimitedSizeText && (
                          <>
                            <span className="mx-1.5 text-gray-500">·</span>
                            {unlimitedSizeText}
                          </>
                        )}
                      {creditExpirySuffix && (
                        <>
                          <span className="mx-1.5 text-gray-500">·</span>
                          {creditExpirySuffix}
                        </>
                      )}
                    </p>
                  )}
              </ReceiptRow>
            ) : giftSpend?.remainder === 0 ? null : (
              <ReceiptRow
                icon={CreditCard}
                strikethrough={cancelled}
                title={
                  giftSpendLines ? (
                    giftSpendLines.remainder
                  ) : slotAmount === 0 ? (
                    'Free'
                  ) : hasCoupon &&
                    order.discount_amount > 0 &&
                    slotOriginalAmount > slotAmount ? (
                    <>
                      <span className="text-gray-400 line-through">
                        {formatAmount(slotOriginalAmount, order.currency)}
                      </span>{' '}
                      {formatAmount(slotAmount, order.currency)}
                    </>
                  ) : (
                    formatAmount(slotAmount, order.currency)
                  )
                }
              >
                {slotAmount === 0 && !cancelled && activeQuantity > 1 && (
                  <p className="mt-1 text-sm text-gray-600">
                    {activeQuantity} spots
                  </p>
                )}
                {slotAmount > 0 && (
                  <p className="mt-1 text-sm text-gray-600">
                    {cancelled ? (
                      <>
                        {slotRefundedAmount > 0
                          ? 'Refunded · Allow 5–10 business days for the refund to appear. Contact your bank with any questions.'
                          : 'Your cancellation is recorded. If a refund is due, it will be processed by the studio.'}
                        {hasCoupon && order.discount_amount > 0 && (
                          <>
                            {' · '}
                            {order.discount_percentage > 0
                              ? `${order.discount_percentage}% off`
                              : `${formatAmount(order.discount_amount, order.currency)} off`}
                            {coupon_code && ` (${coupon_code})`}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        Paid
                        {hasCoupon && order.discount_amount > 0 && (
                          <>
                            {' · '}
                            {order.discount_percentage > 0
                              ? `${order.discount_percentage}% off`
                              : `${formatAmount(order.discount_amount, order.currency)} off`}
                            {coupon_code && ` (${coupon_code})`}
                          </>
                        )}
                        {activeQuantity > 1 && (
                          <>
                            {' · '}
                            {activeQuantity} spots
                          </>
                        )}
                      </>
                    )}
                  </p>
                )}
              </ReceiptRow>
            ))
          )}

          {guestsHere.map((guest) => (
            <ReceiptGuestRow
              key={guest.guest_order_id}
              guest={guest}
              subline="Your guest"
              onRemove={
                canRemoveGuests ? () => openRemoveGuest(guest) : undefined
              }
            />
          ))}

          {guestAdd && order.guest_pass_add && (
            <div className="flex items-center gap-4 max-[372px]:flex-col max-[372px]:items-stretch">
              <ReceiptRow icon={UserPlus} title="Bring a friend">
                <p className="mt-1 text-sm text-gray-600">
                  {bringGuestHint(
                    {
                      left: order.guest_pass_add.passes_left,
                      new_guests_only: order.guest_pass_add.new_guests_only,
                    },
                    tenant?.name
                  )}
                </p>
              </ReceiptRow>
              <Button
                variant="secondary-gray"
                className="ml-auto shrink-0 justify-center"
                onClick={() => setAddGuestTarget(guestAdd)}
              >
                Add a guest
              </Button>
            </div>
          )}

          {apartGuests.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                {apartGuestsHeading(apartGuests.length)}
              </p>
              {apartGuests.map((guest) => (
                <ReceiptGuestRow
                  key={guest.guest_order_id}
                  guest={guest}
                  subline={formatClassDateTime(
                    guest.start_time,
                    timeFormat,
                    getUserTimezone(),
                    'short'
                  )}
                  onRemove={
                    !isLinkHolder &&
                    isBeforeChangeCutoff(
                      new Date(guest.start_time),
                      modificationNoticeHours
                    ) &&
                    canRemoveApartGuest({ minSpots, guest, apartGuests })
                      ? () => openRemoveGuest(guest)
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>

        {!completed && <Separator className="my-6" />}

        {completed ? (
          <div className="mt-6 flex flex-col items-center gap-3">
            <Button
              size="lg"
              className="w-full justify-center"
              onClick={() =>
                router.push(
                  canRebookSame
                    ? `/classes/${classData.slug}`
                    : '/classes?from=receipt'
                )
              }
            >
              {canRebookSame ? 'Book another class' : 'See all classes'}
            </Button>
          </div>
        ) : !cancelled ? (
          <div className="space-y-5 sm:space-y-6">
            <div className="flex flex-col gap-3">
              <p className="text-base font-semibold text-gray-900">
                Book another {canRebookSame ? classData.name : 'class'}?
              </p>
              {canRebookSame && (
                <Button
                  size="lg"
                  className="w-full justify-center"
                  onClick={() => router.push(`/classes/${classData.slug}`)}
                >
                  Book another
                </Button>
              )}
              <Button
                variant={canRebookSame ? 'secondary-gray' : 'primary'}
                size="lg"
                className="w-full justify-center"
                onClick={() => router.push('/classes?from=receipt')}
              >
                See all classes
              </Button>
            </div>

            {!isLinkHolder && !guestOf && (
              <>
                <Separator />

                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Manage booking
                  </p>

                  <div className="flex gap-4 max-[372px]:flex-col">
                    {showActionButtons ? (
                      <>
                        {!isOneTime && (
                          <Button
                            variant="secondary-gray"
                            leadingIcon={<RefreshIcon />}
                            size="lg"
                            className="flex-1 justify-center"
                            onClick={handleRescheduleClass}
                          >
                            Reschedule
                          </Button>
                        )}
                        <Button
                          variant="secondary-gray"
                          leadingIcon={<X className="h-4 w-4" />}
                          size="lg"
                          className="flex-1 justify-center"
                          onClick={openCancelModal}
                        >
                          Cancel booking
                        </Button>
                      </>
                    ) : (
                      <>
                        {/* The locked pair outlives the live pair:
                            showActionButtons goes false for good once the class
                            has started, so an ungated Reschedule here is the one
                            every one-time booker ends up looking at. */}
                        {!isOneTime && (
                          <LockedAction
                            label="Reschedule"
                            notice={lockedNotice}
                            open={openPopover === 'reschedule'}
                            onOpenChange={(open) =>
                              setOpenPopover(open ? 'reschedule' : null)
                            }
                          />
                        )}
                        <LockedAction
                          label="Cancel"
                          notice={lockedNotice}
                          open={openPopover === 'cancel'}
                          onOpenChange={(open) =>
                            setOpenPopover(open ? 'cancel' : null)
                          }
                        />
                      </>
                    )}
                  </div>

                  {!showActionButtons && (
                    <div className="w-full rounded-[10px] bg-gray-100 px-6 py-3 text-center text-sm leading-5 text-gray-900">
                      {hasClassStarted
                        ? 'This class has already started.'
                        : `A booking can only be ${changeVerb} ${noticeHours} before the class starts.`}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="flex w-full flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                Add to calendar
              </p>
              <CalendarIntegration
                title={classData.name}
                startTime={startDate}
                endTime={endDate}
                location={classData.location}
                description={classData.description ?? ''}
                icsUrl={
                  order.ical_token
                    ? classCalendarFileUrl(
                        order_id,
                        order.ical_token,
                        orderItem
                      )
                    : null
                }
              />
            </div>

            <InstallAppCard />
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-base font-semibold text-gray-900">
              Book another {canRebookSame ? classData.name : 'class'}?
            </p>
            <div className="flex flex-col gap-4 sm:flex-row">
              {canRebookSame && (
                <Button
                  size="lg"
                  className="w-full justify-center sm:flex-1"
                  onClick={() => router.push(`/classes/${classData.slug}`)}
                >
                  Book another class
                </Button>
              )}
              <Button
                variant={canRebookSame ? 'secondary-gray' : 'primary'}
                size="lg"
                className="w-full justify-center sm:flex-1"
                onClick={() => router.push('/classes?from=receipt')}
              >
                See all classes
              </Button>
            </div>
          </div>
        )}
      </div>

      {!isAuthenticated && (
        <>
          <Separator />
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-base font-semibold text-gray-900">
                Manage your classes & credits
              </p>
              <p className="text-sm text-gray-700">
                Log in with your email. No password needed.
              </p>
            </div>
            <Button
              className="w-full justify-center"
              onClick={() => setActiveModal('login')}
              variant="secondary-gray"
              size="lg"
            >
              Log in
            </Button>
          </div>
        </>
      )}

      {!isLinkHolder && !guestOf && (
        <CancelClassModal
          isOpen={isCancelClassModalOpen}
          onOpenChange={setIsCancelClassModalOpen}
          activeQuantity={activeQuantity}
          minSpots={minSpots}
          totalQuantity={quantity}
          cancelSpots={cancelQty}
          onCancelQuantityChange={setCancelQuantity}
          isCreditCodeApplied={isCreditCodeApplied}
          isFromPack={isFromPack}
          isCreditSourceActive={isCreditSourceActive}
          isTopupBooking={isTopupBooking}
          isTopupRefundable={isTopupRefundable}
          classPackClassesLeft={
            class_pack_code ? getAvailableCredits(class_pack_code) : null
          }
          subscriptionClassesLeft={
            subscription_code ? getAvailableSubCredits(subscription_code) : null
          }
          topupCredits={topupCreditsForPlan}
          isFreeBooking={isFreeBooking}
          giftCardRemainder={giftSpend?.remainder ?? null}
          giftCardRefundsToCard={giftCardRefundsToCard}
          guestNames={guestsHere.map((guest) => guest.first_name)}
          cancelGuests={cancelGuests}
          onCancelGuestsChange={setCancelGuestsChoice}
          cancelGuestsLocked={cancelGuestsLocked}
          guestPassReturns={isCreditSourceActive}
          onConfirm={handleCancelClass}
          isProcessing={isCancelling}
        />
      )}

      {removingGuest && (
        <RemoveGuestModal
          orderId={order_id}
          guest={removingGuest}
          hostBooked={
            activeQuantity > 0 &&
            !apartGuests.some(
              (guest) => guest.guest_order_id === removingGuest.guest_order_id
            )
          }
          passReturns={isCreditSourceActive}
          classId={classData.class_id}
          className={classData.name}
          isFromPack={isFromPack}
          onClose={() => setRemovingGuest(null)}
          onRecheck={invalidateAfterGuestChange}
          onLoginRequired={() => {
            setRemovingGuest(null);
            setActiveModal('login');
          }}
        />
      )}

      {addGuestTarget && (
        <AddGuestModal
          orderId={order_id}
          occurrence={addGuestTarget}
          classId={classData.class_id}
          className={classData.name}
          whenLabel={formatClassDateTime(
            addGuestTarget.start_time,
            timeFormat,
            getUserTimezone(),
            'short'
          )}
          minGuests={(guestAdd ?? addGuestTarget).min_guests ?? 1}
          maxGuests={(guestAdd ?? addGuestTarget).max_guests}
          newGuestsOnly={order.guest_pass_add?.new_guests_only ?? false}
          source={isFromPack ? 'class_pack' : 'subscription'}
          hostEmail={user?.email ?? order.user?.email}
          hostPhone={user ?? order.user}
          onClose={() => setAddGuestTarget(null)}
          onAdded={(added) => {
            const { title, description } = guestsAddedToast(added);
            toast.success(title, { description });
            setAddGuestTarget(null);
            invalidateAfterGuestChange();
          }}
          onRecheck={invalidateAfterGuestChange}
          onLoginRequired={() => {
            setAddGuestTarget(null);
            setActiveModal('login');
          }}
        />
      )}

      {activeModal === 'login' && (
        <LoginDialog
          open={activeModal === 'login'}
          onClose={() => {
            pendingActionRef.current = null;
            setActiveModal(null);
          }}
          onConfirm={() => setActiveModal(null)}
        />
      )}
    </>
  );
};

const ItemlessOrderNotice = ({ order }: { order: Order }) => {
  if (order.payment_status !== 'REFUNDED') {
    return (
      <ReceiptNotice
        icon="unreachable"
        role="alert"
        title={"We couldn't load\nyour booking details"}
      />
    );
  }

  return (
    <ReceiptNotice
      icon="unavailable"
      title={RACE_REFUND_RECEIPT.title}
      description={classRaceRefundNotice(order)}
    />
  );
};

// An order with no items is a checkout that lost the spot race: the backend
// refunds it and writes no booking. An ordinary cancellation keeps its items
// with a CANCELLED booking_status, so it never lands here.
export const ClassOrderReceipt = (props: ClassOrderReceiptProps) => {
  const searchParams = useSearchParams();
  // Read once, so the class time chosen cannot change while the receipt is
  // open because one of the order's class times ended meanwhile.
  const [openedAt] = useState(() => new Date());
  const classTime = selectReceiptItems(
    props.order.class_order_items ?? [],
    {
      slotId: searchParams?.get('slotId') ?? null,
      startTime: searchParams?.get('startTime') ?? null,
    },
    !props.cancelled,
    openedAt
  );

  if (!classTime) {
    return <ItemlessOrderNotice order={props.order} />;
  }

  return <ClassOrderReceiptBody {...props} classTime={classTime} />;
};
